import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import { X } from "lucide-react";

/**
 * Global zoom modal. Mount ONCE at the App root. Listens for `zoom-media`
 * custom events and shows the contained HTML (already-rendered SVG / img /
 * table) in a centered overlay.
 *
 * Behaviour spec:
 *   - 90% viewport max width/height
 *   - Click overlay or × → close
 *   - ESC → close
 *   - Click inside modal content → does NOT close
 *   - Body scroll-locked while open
 *   - Focus returns to previously focused element on close
 *   - Debounced trigger (ignores stacked events within 250ms)
 */
export default function ZoomModal() {
  const [content, setContent] = useState(null); // { html, label }
  const triggerEl = useRef(null);
  const lastOpen = useRef(0);
  const closeBtnRef = useRef(null);

  const close = useCallback(() => {
    setContent(null);
    // Return focus to the element that opened the modal
    setTimeout(() => {
      try { triggerEl.current?.focus?.(); } catch { /* trigger may have unmounted */ }
      triggerEl.current = null;
    }, 0);
  }, []);

  useEffect(() => {
    const onZoom = (e) => {
      const now = Date.now();
      if (now - lastOpen.current < 250) return; // debounce stacking
      lastOpen.current = now;
      triggerEl.current = document.activeElement;
      setContent({ html: e.detail?.html || "", label: e.detail?.label || "Zoomed view" });
    };
    window.addEventListener("zoom-media", onZoom);
    return () => window.removeEventListener("zoom-media", onZoom);
  }, []);

  // ESC + scroll lock + focus management
  useEffect(() => {
    if (!content) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      // Simple focus trap — keep tab focus on the close button
      if (e.key === "Tab") {
        e.preventDefault();
        closeBtnRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    // Initial focus
    setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [content, close]);

  if (!content) return null;

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={content.label}
      data-testid="zoom-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 zoom-fade-in"
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      onClick={close}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh] overflow-auto rounded-xl bg-zinc-950 border border-zinc-800 shadow-zoom"
        style={{ padding: 24 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="zoom-modal-content"
      >
        <button
          ref={closeBtnRef}
          onClick={close}
          aria-label="Close zoomed view"
          data-testid="zoom-modal-close"
          className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-emerald-500/40 flex items-center justify-center transition-colors shadow-lg"
        >
          <X className="w-4 h-4" />
        </button>
        <div
          className="zoom-content"
          // The cloned HTML originates from already-rendered chat content
          // (react-markdown output or mermaid.render SVG, both sanitized).
          // We re-run DOMPurify here as defense-in-depth before injection.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content.html, {
              USE_PROFILES: { svg: true, svgFilters: true, html: true },
              ADD_TAGS: ["foreignObject"],
              FORBID_TAGS: ["script", "style", "iframe", "object", "embed"],
              FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
            }),
          }}
        />
      </div>
    </div>,
    document.body
  );
}
